import { h, ref, type Component, type Ref, type VNode } from 'vue';
import { NIcon, useDialog, type DropdownOption } from 'naive-ui';
import { sendPasswordResetEmail } from 'firebase/auth';
import type { UserInDb } from '@/api';
import {
  IconLock,
  IconLogout,
  IconMail,
  IconRefresh,
  IconUserRemove,
} from '@/components/design/icons';
import { getFirebaseAuth } from '@/config/firebase';
import { useGaiaToast } from '@/composables/useGaiaToast';
import { useAuthStore } from '@/stores/auth';
import { useOrganizationStore } from '@/stores/organizationStore';
import { logger } from '@/utils/logger';

/** A pending remove (someone else) or leave (yourself), confirmed in OrganisationSeveranceModal. */
export interface PendingSeverance {
  mode: 'leave' | 'remove';
  uid: string;
  email: string;
}

interface MemberActionHooks {
  /** A member was deactivated or reactivated. */
  onChanged: () => Promise<void> | void;
  /** A member was removed from the organisation. */
  onRemoved: () => Promise<void> | void;
}

export interface MemberActions {
  menuOptions: (user: UserInDb) => DropdownOption[];
  run: (user: UserInDb, key: string) => void;
  severance: Ref<PendingSeverance | null>;
  severanceLoading: Ref<boolean>;
  severanceError: Ref<string | null>;
  closeSeverance: () => void;
  confirmSeverance: () => Promise<void>;
}

function menuIcon(icon: Component): () => VNode {
  return () => h(NIcon, null, { default: () => h(icon) });
}

/**
 * What an org admin can do to a member — password reset, deactivate or
 * reactivate, remove (or leave, on your own row) — shared by the People list's
 * row menu and the member page's header menu, so both offer the same things
 * with the same confirmations. The host renders OrganisationSeveranceModal
 * from `severance` and says what to refresh afterwards.
 */
export function useMemberActions(hooks: MemberActionHooks): MemberActions {
  const authStore = useAuthStore();
  const organizationStore = useOrganizationStore();
  const message = useGaiaToast();
  const dialog = useDialog();

  const severance = ref<PendingSeverance | null>(null);
  const severanceLoading = ref(false);
  const severanceError = ref<string | null>(null);

  function menuOptions(user: UserInDb): DropdownOption[] {
    const isSelf = authStore.backendUser?.uid === user.uid;
    const options: DropdownOption[] = [
      { key: 'reset', label: 'Send password reset', icon: menuIcon(IconMail) },
    ];
    // Deactivation is platform-wide; nobody deactivates themselves.
    if (!isSelf) {
      options.push({
        key: 'toggle-active',
        label: user.is_active ? 'Deactivate' : 'Reactivate',
        icon: menuIcon(user.is_active ? IconLock : IconRefresh),
      });
    }
    // Removing others is open to any admin; leaving is only for Gaia admins, who
    // are guests in member organisations.
    if (!isSelf || authStore.userPermissions.is_platform_admin) {
      options.push(
        { type: 'divider', key: 'divider' },
        {
          key: isSelf ? 'leave' : 'remove',
          label: isSelf ? 'Leave organisation' : 'Remove from organisation',
          icon: menuIcon(isSelf ? IconLogout : IconUserRemove),
        }
      );
    }
    return options;
  }

  async function sendPasswordReset(email: string): Promise<void> {
    try {
      await sendPasswordResetEmail(getFirebaseAuth(), email);
      message.success(`Password reset email sent to ${email}`);
    } catch (error: unknown) {
      message.error(error instanceof Error ? error.message : 'Failed to send password reset email');
      logger.error('Failed to send password reset email', error);
    }
  }

  async function toggleActive(user: UserInDb): Promise<void> {
    const nextState = !user.is_active;
    const success = await organizationStore.setUserActiveState(user.uid, nextState);
    if (success) {
      message.success(nextState ? `${user.email} reactivated` : `${user.email} deactivated`);
      await hooks.onChanged();
    } else if (organizationStore.error) {
      message.error(organizationStore.error);
    }
  }

  function run(user: UserInDb, key: string): void {
    switch (key) {
      case 'reset': {
        dialog.info({
          title: 'Send password reset',
          content: `${user.email} will get an email with a link to choose a new password.`,
          positiveText: 'Send email',
          negativeText: 'Cancel',
          onPositiveClick: () => sendPasswordReset(user.email),
        });
        break;
      }
      case 'toggle-active': {
        dialog.warning({
          title: user.is_active ? `Deactivate ${user.email}?` : `Reactivate ${user.email}?`,
          content: user.is_active
            ? 'They will be blocked from signing in everywhere on the platform, not just in this organisation.'
            : 'They will be able to sign in again.',
          positiveText: user.is_active ? 'Deactivate' : 'Reactivate',
          negativeText: 'Cancel',
          positiveButtonProps: user.is_active ? { type: 'warning' } : undefined,
          onPositiveClick: () => toggleActive(user),
        });
        break;
      }
      case 'remove':
      case 'leave': {
        severanceError.value = null;
        severance.value = { mode: key, uid: user.uid, email: user.email };
        break;
      }
      // No default
    }
  }

  function closeSeverance(): void {
    if (severanceLoading.value) return;
    severance.value = null;
  }

  async function confirmSeverance(): Promise<void> {
    const pending = severance.value;
    if (!pending || severanceLoading.value) return;
    if (!authStore.userPermissions.is_org_admin) {
      message.error('You do not have permission to change memberships.');
      return;
    }
    severanceLoading.value = true;
    severanceError.value = null;

    if (pending.mode === 'remove') {
      const success = await organizationStore.removeUserFromOrganization(pending.uid);
      severanceLoading.value = false;
      if (!success) {
        severanceError.value = organizationStore.error ?? 'Failed to remove user';
        return;
      }
      severance.value = null;
      message.success(`Removed ${pending.email} from the organisation`);
      await hooks.onRemoved();
      return;
    }

    // Leaving as a Gaia admin: return them to the admin organisation so they
    // are not left without a context, then start over — every cached store is
    // scoped to the organisation just left.
    const profile = await organizationStore.leaveOrganization(
      organizationStore.activeOrganization?.uid ?? ''
    );
    if (!profile) {
      severanceLoading.value = false;
      severanceError.value = organizationStore.error ?? 'Failed to leave organization';
      return;
    }
    await organizationStore.switchToAdminOrganization();
    window.location.reload();
  }

  return {
    menuOptions,
    run,
    severance,
    severanceLoading,
    severanceError,
    closeSeverance,
    confirmSeverance,
  };
}
